// API Error Handler Utility
// Provides consistent error message extraction from API responses

/**
 * Extract a human-readable error message from API error responses
 * Handles various error formats: string, array (Pydantic), object
 */
export const getApiErrorMessage = (error, defaultMessage = 'Произошла ошибка') => {
  // Network error (no response)
  if (!error.response) {
    if (error.message === 'Network Error') {
      return 'Ошибка сети. Проверьте подключение к интернету';
    }
    return error.message || defaultMessage;
  }

  const { data, status } = error.response;

  // Handle common HTTP status codes
  if (status === 401) {
    return 'Сессия истекла. Пожалуйста, войдите снова';
  }
  if (status === 403) {
    return 'Доступ запрещён';
  }
  if (status === 404) {
    return 'Ресурс не найден';
  }
  if (status === 429) {
    return 'Слишком много запросов. Попробуйте позже';
  }
  if (status >= 500) {
    return 'Ошибка сервера. Попробуйте позже';
  }

  // Extract message from response data
  if (!data) {
    return defaultMessage;
  }

  // FastAPI/Pydantic detail format
  if (data.detail) {
    const detail = data.detail;

    // String message
    if (typeof detail === 'string') {
      return detail;
    }

    // Array of validation errors (Pydantic format)
    if (Array.isArray(detail)) {
      return detail
        .map(err => {
          // Standard Pydantic format: { loc: [], msg: '', type: '' }
          if (err.msg) {
            const field = err.loc?.slice(-1)[0] || '';
            return field ? `${field}: ${err.msg}` : err.msg;
          }
          // Alternative format
          if (err.message) {
            return err.message;
          }
          return String(err);
        })
        .join('. ');
    }

    // Object with message
    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
  }

  // Standard message field
  if (data.message) {
    return data.message;
  }

  // Standard error field
  if (data.error) {
    return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
  }

  return defaultMessage;
};

/**
 * Check if error is a validation error (422)
 */
export const isValidationError = (error) => {
  return error.response?.status === 422;
};

/**
 * Check if error is an authentication error (401)
 */
export const isAuthError = (error) => {
  return error.response?.status === 401;
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error) => {
  return !error.response && error.message === 'Network Error';
};

/**
 * Format validation errors into field-specific messages
 * Returns object with field names as keys and error messages as values
 */
export const getValidationErrors = (error) => {
  const errors = {};
  
  if (!error.response?.data?.detail || !Array.isArray(error.response.data.detail)) {
    return errors;
  }

  error.response.data.detail.forEach(err => {
    if (err.loc && err.loc.length > 0) {
      const field = err.loc[err.loc.length - 1];
      errors[field] = err.msg;
    }
  });

  return errors;
};

export default {
  getApiErrorMessage,
  isValidationError,
  isAuthError,
  isNetworkError,
  getValidationErrors,
};
